import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { AppShell } from "@/components/layout/AppShell";
import {
  TenantsAPI, InstitutionsAPI, YearsAPI, ClassesAPI, SectionsAPI,
} from "@/lib/sprint2-api";
import { useAuth } from "@/context/AuthContext";
import { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger,
} from "@/components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { toast } from "sonner";
import { Plus, Loader2, Trash2, Layers, Users, Upload, UserCheck } from "lucide-react";
import { Link } from "react-router-dom";
import { TimetableAPI } from "@/lib/timetable-api";

export default function ClassesSections() {
  const { user } = useAuth();
  const isSuper = user?.roles?.includes("super_admin");
  const queryClient = useQueryClient();

  const [tenantId, setTenantId] = useState("");
  const [institutionId, setInstitutionId] = useState("");
  const [yearId, setYearId] = useState("");
  const [classId, setClassId] = useState("");

  const tenantsQuery = useQuery({
    queryKey: ["tenants"],
    queryFn: () => TenantsAPI.list(),
    enabled: isSuper,
  });
  const effectiveTenantId = isSuper
    ? (tenantId || tenantsQuery.data?.[0]?.id || "")
    : (user?.tenant_id || "");
  const tenantParam = isSuper && effectiveTenantId ? { tenant_id: effectiveTenantId } : undefined;

  const institutionsQuery = useQuery({
    queryKey: ["institutions", effectiveTenantId],
    queryFn: () => InstitutionsAPI.list(tenantParam),
    enabled: !!effectiveTenantId,
  });
  const effectiveInstitutionId =
    (institutionsQuery.data?.some((i) => i.id === institutionId) ? institutionId : "") ||
    institutionsQuery.data?.[0]?.id ||
    "";

  const yearsQuery = useQuery({
    queryKey: ["years", effectiveInstitutionId],
    queryFn: () => YearsAPI.list({ institution_id: effectiveInstitutionId, ...(tenantParam || {}) }),
    enabled: !!effectiveInstitutionId,
  });
  const effectiveYearId = (() => {
    const list = yearsQuery.data || [];
    if (list.some((y) => y.id === yearId)) return yearId;
    return list.find((y) => y.is_current)?.id || list[0]?.id || "";
  })();

  const classesQuery = useQuery({
    queryKey: ["classes", effectiveYearId],
    queryFn: () => ClassesAPI.list({ academic_year_id: effectiveYearId, ...(tenantParam || {}) }),
    enabled: !!effectiveYearId,
  });
  const effectiveClassId =
    (classesQuery.data?.some((c) => c.id === classId) ? classId : "") ||
    classesQuery.data?.[0]?.id ||
    "";

  const sectionsQuery = useQuery({
    queryKey: ["sections", effectiveClassId],
    queryFn: () => SectionsAPI.list({ class_id: effectiveClassId, ...(tenantParam || {}) }),
    enabled: !!effectiveClassId,
  });

  const invalidate = (key) => queryClient.invalidateQueries({ queryKey: key });

  return (
    <AppShell>
      <div className="p-8" data-testid="classes-sections-page">
        <div className="flex items-start justify-between mb-6 gap-4 flex-wrap">
          <div>
            <h1 className="font-display font-extrabold tracking-tight text-4xl">Classes &amp; Sections</h1>
            <p className="text-sm text-muted-foreground mt-2 max-w-2xl">
              Compose the cohorts: <span className="font-mono text-xs px-1 bg-secondary rounded">classes</span> sit inside an academic year, and each class is split into one or more <span className="font-mono text-xs px-1 bg-secondary rounded">sections</span>.
            </p>
          </div>
          <Link to="/bulk-import?resource=classes">
            <Button variant="outline" data-testid="cs-bulk-import">
              <Upload className="h-3.5 w-3.5 mr-1.5" /> Bulk import
            </Button>
          </Link>
        </div>

        {/* Scope */}
        <div className="flex flex-wrap gap-3 mb-6 p-4 border border-border rounded-md bg-card">
          {isSuper && (
            <Scope
              label="Tenant"
              value={effectiveTenantId}
              onChange={(v) => { setTenantId(v); setInstitutionId(""); setYearId(""); setClassId(""); }}
              options={tenantsQuery.data || []}
              testid="cs-scope-tenant"
            />
          )}
          <Scope
            label="Institution"
            value={effectiveInstitutionId}
            onChange={(v) => { setInstitutionId(v); setYearId(""); setClassId(""); }}
            options={institutionsQuery.data || []}
            testid="cs-scope-institution"
          />
          <Scope
            label="Academic year"
            value={effectiveYearId}
            onChange={(v) => { setYearId(v); setClassId(""); }}
            options={(yearsQuery.data || []).map((y) => ({ id: y.id, name: y.name + (y.is_current ? " · current" : "") }))}
            disabled={!effectiveInstitutionId}
            testid="cs-scope-year"
          />
        </div>

        <Tabs defaultValue="classes">
          <TabsList>
            <TabsTrigger value="classes" data-testid="cs-tab-classes"><Layers className="h-3.5 w-3.5 mr-1.5" />Classes</TabsTrigger>
            <TabsTrigger value="sections" data-testid="cs-tab-sections"><Users className="h-3.5 w-3.5 mr-1.5" />Sections</TabsTrigger>
          </TabsList>

          <TabsContent value="classes" className="mt-6">
            <ClassesPanel
              institutionId={effectiveInstitutionId}
              yearId={effectiveYearId}
              classes={classesQuery.data}
              loading={classesQuery.isLoading}
              onRefresh={() => invalidate(["classes", effectiveYearId])}
            />
          </TabsContent>

          <TabsContent value="sections" className="mt-6">
            <SectionsPanel
              classId={effectiveClassId}
              classes={classesQuery.data || []}
              selectedClassId={effectiveClassId}
              onSelectClass={setClassId}
              sections={sectionsQuery.data}
              loading={sectionsQuery.isLoading}
              onRefresh={() => invalidate(["sections", effectiveClassId])}
            />
          </TabsContent>
        </Tabs>
      </div>
    </AppShell>
  );
}

function Scope({ label, value, onChange, options, disabled, testid }) {
  return (
    <div className="space-y-1.5 min-w-[180px]">
      <Label className="text-[10px] font-semibold uppercase tracking-wider">{label}</Label>
      <Select value={value} onValueChange={onChange} disabled={disabled || !options.length}>
        <SelectTrigger className="w-full" data-testid={testid}>
          <SelectValue placeholder={disabled ? "—" : `Select ${label.toLowerCase()}`} />
        </SelectTrigger>
        <SelectContent>
          {options.map((o) => <SelectItem key={o.id} value={o.id}>{o.name}</SelectItem>)}
        </SelectContent>
      </Select>
    </div>
  );
}

function ClassesPanel({ institutionId, yearId, classes, loading, onRefresh }) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: "", code: "", order: 0 });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setError(""); setBusy(true);
    try {
      await ClassesAPI.create({
        institution_id: institutionId,
        academic_year_id: yearId,
        ...form,
        order: Number(form.order) || 0,
      });
      toast.success("Class added");
      setForm({ name: "", code: "", order: 0 });
      setOpen(false);
      onRefresh();
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail));
    } finally { setBusy(false); }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this class and its sections / subjects?")) return;
    try { await ClassesAPI.remove(id); toast.success("Class removed"); onRefresh(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  return (
    <PanelShell
      title="Classes"
      count={classes?.length || 0}
      action={
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button disabled={!yearId} data-testid="cs-create-class"><Plus className="h-3.5 w-3.5 mr-1.5" />Class</Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader><DialogTitle>New class</DialogTitle></DialogHeader>
            <form onSubmit={submit} className="space-y-3">
              <Field label="Name (e.g. Grade 5)">
                <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required data-testid="cs-class-name" />
              </Field>
              <div className="grid grid-cols-2 gap-3">
                <Field label="Code">
                  <Input value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value })} placeholder="G5" className="font-mono" data-testid="cs-class-code" />
                </Field>
                <Field label="Display order">
                  <Input type="number" value={form.order} onChange={(e) => setForm({ ...form, order: e.target.value })} data-testid="cs-class-order" />
                </Field>
              </div>
              {error && <div className="text-sm text-destructive border border-destructive/30 bg-destructive/5 rounded-md px-3 py-2">{error}</div>}
              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
                <Button type="submit" disabled={busy} data-testid="cs-class-submit">{busy && <Loader2 className="h-4 w-4 animate-spin mr-2" />}Add</Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      }
    >
      {!yearId ? <Empty msg="Pick an academic year to manage its classes." /> :
        loading ? <Loading /> :
        classes?.length ? (
          <Table>
            <TableHeader><TableRow><TableHead>Class</TableHead><TableHead>Code</TableHead><TableHead>Order</TableHead><TableHead /></TableRow></TableHeader>
            <TableBody>
              {classes.map((c) => (
                <TableRow key={c.id} data-testid={`cs-class-row-${c.id}`}>
                  <TableCell className="font-medium">{c.name}</TableCell>
                  <TableCell className="font-mono text-xs">{c.code || "—"}</TableCell>
                  <TableCell className="text-sm text-muted-foreground">{c.order}</TableCell>
                  <TableCell className="text-right">
                    <Button size="icon" variant="ghost" onClick={() => remove(c.id)} data-testid={`cs-class-del-${c.id}`}>
                      <Trash2 className="h-3.5 w-3.5 text-destructive" />
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        ) : <Empty msg="No classes yet. Add Grade 5, Grade 6, FY BSc, etc." />}
    </PanelShell>
  );
}

function SectionsPanel({ classId, classes, selectedClassId, onSelectClass, sections, loading, onRefresh }) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: "", capacity: 40 });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [ctPickerFor, setCtPickerFor] = useState(null); // section object being edited

  // Institution id resolves via the selected class, so we can pull the pool
  // of teachers for the CT picker.
  const selectedClass = classes.find((c) => c.id === classId);
  const institutionId = selectedClass?.institution_id;
  const teachersQuery = useQuery({
    queryKey: ["cs-teachers", institutionId],
    queryFn: () => TimetableAPI.teachers(institutionId),
    enabled: !!institutionId,
  });

  const submit = async (e) => {
    e.preventDefault();
    setError(""); setBusy(true);
    try {
      await SectionsAPI.create({ class_id: classId, name: form.name, capacity: Number(form.capacity) || 40 });
      toast.success("Section added");
      setForm({ name: "", capacity: 40 });
      setOpen(false);
      onRefresh();
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail));
    } finally { setBusy(false); }
  };

  const remove = async (id) => {
    try { await SectionsAPI.remove(id); toast.success("Section removed"); onRefresh(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const assignCT = async (sectionId, teacherId) => {
    try {
      await SectionsAPI.update(sectionId, { class_teacher_id: teacherId });
      toast.success(teacherId ? "Class teacher assigned" : "Class teacher cleared");
      setCtPickerFor(null);
      onRefresh();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Update failed");
    }
  };

  return (
    <PanelShell
      title="Sections"
      count={sections?.length || 0}
      action={
        <div className="flex items-center gap-2">
          <Select value={selectedClassId} onValueChange={onSelectClass} disabled={!classes.length}>
            <SelectTrigger className="w-[180px]" data-testid="cs-section-class-picker">
              <SelectValue placeholder="Pick a class" />
            </SelectTrigger>
            <SelectContent>
              {classes.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
            </SelectContent>
          </Select>
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button disabled={!classId} data-testid="cs-create-section"><Plus className="h-3.5 w-3.5 mr-1.5" />Section</Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader><DialogTitle>New section</DialogTitle></DialogHeader>
              <form onSubmit={submit} className="space-y-3">
                <div className="grid grid-cols-2 gap-3">
                  <Field label="Name (e.g. A)"><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required data-testid="cs-section-name" /></Field>
                  <Field label="Capacity"><Input type="number" min="1" value={form.capacity} onChange={(e) => setForm({ ...form, capacity: e.target.value })} data-testid="cs-section-capacity" /></Field>
                </div>
                {error && <div className="text-sm text-destructive border border-destructive/30 bg-destructive/5 rounded-md px-3 py-2">{error}</div>}
                <DialogFooter>
                  <Button type="button" variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
                  <Button type="submit" disabled={busy} data-testid="cs-section-submit">{busy && <Loader2 className="h-4 w-4 animate-spin mr-2" />}Add</Button>
                </DialogFooter>
              </form>
            </DialogContent>
          </Dialog>
        </div>
      }
    >
      {!classId ? <Empty msg="Pick a class to manage sections." /> :
        loading ? <Loading /> :
        sections?.length ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 p-4">
            {sections.map((s) => (
              <div key={s.id} className="border border-border rounded-md p-4 bg-card flex flex-col gap-2 relative group" data-testid={`cs-section-card-${s.id}`}>
                <div className="flex items-start justify-between gap-2">
                  <div>
                    <div className="font-display font-extrabold text-3xl tracking-tight">{s.name}</div>
                    <div className="text-[10px] uppercase tracking-wider text-muted-foreground">Capacity {s.capacity}</div>
                  </div>
                  <Button size="icon" variant="ghost" className="opacity-0 group-hover:opacity-100 transition" onClick={() => remove(s.id)} data-testid={`cs-section-del-${s.id}`}>
                    <Trash2 className="h-3.5 w-3.5 text-destructive" />
                  </Button>
                </div>
                {/* Class Teacher row */}
                <div className="mt-1 pt-2.5 border-t border-border">
                  <div className="text-[9px] uppercase tracking-wider font-semibold text-muted-foreground mb-1 flex items-center gap-1">
                    <UserCheck className="h-3 w-3" /> Class Teacher
                  </div>
                  {s.class_teacher ? (
                    <div className="flex items-center justify-between gap-2">
                      <div className="min-w-0">
                        <div className="text-sm font-medium truncate" data-testid={`cs-ct-name-${s.id}`}>{s.class_teacher.name}</div>
                        {s.class_teacher.phone && (
                          <a href={`tel:${s.class_teacher.phone}`} className="text-[11px] text-muted-foreground font-mono hover:text-primary">{s.class_teacher.phone}</a>
                        )}
                      </div>
                      <Button size="sm" variant="outline" className="text-xs h-7 shrink-0" onClick={() => setCtPickerFor(s)} data-testid={`cs-ct-change-${s.id}`}>Change</Button>
                    </div>
                  ) : (
                    <Button size="sm" variant="outline" className="w-full text-xs h-7" onClick={() => setCtPickerFor(s)} data-testid={`cs-ct-assign-${s.id}`}>
                      <UserCheck className="h-3 w-3 mr-1.5" /> Assign class teacher
                    </Button>
                  )}
                </div>
              </div>
            ))}
          </div>
        ) : <Empty msg="No sections yet for this class." />}
      {/* Class Teacher picker modal */}
      <Dialog open={!!ctPickerFor} onOpenChange={(v) => { if (!v) setCtPickerFor(null); }}>
        <DialogContent data-testid="cs-ct-picker-dialog">
          <DialogHeader>
            <DialogTitle>Class Teacher · Section {ctPickerFor?.name}</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <p className="text-xs text-muted-foreground">
              Pick the homeroom teacher for this section. They will get a <code className="bg-secondary px-1 rounded text-[10px]">class_teacher</code> role and a &quot;My Class&quot; shortcut on their dashboard.
            </p>
            {teachersQuery.isLoading ? (
              <div className="text-center py-6"><Loader2 className="h-4 w-4 animate-spin inline" /></div>
            ) : (teachersQuery.data || []).length === 0 ? (
              <Empty msg="No teachers found in this institution yet." />
            ) : (
              <div className="max-h-[320px] overflow-y-auto border border-border rounded-md divide-y">
                {(teachersQuery.data || []).map((t) => {
                  const selected = ctPickerFor?.class_teacher_id === t.id;
                  return (
                    <button key={t.id} type="button"
                      onClick={() => assignCT(ctPickerFor.id, t.id)}
                      className={`w-full flex items-center justify-between gap-2 px-3 py-2 text-left hover:bg-muted/50 transition ${selected ? "bg-primary/10" : ""}`}
                      data-testid={`cs-ct-opt-${t.id}`}>
                      <div className="min-w-0">
                        <div className="text-sm font-medium truncate">{t.name}</div>
                        <div className="text-[10px] text-muted-foreground truncate">{t.email}</div>
                      </div>
                      {selected && <Badge variant="outline" className="text-[10px]">Current</Badge>}
                    </button>
                  );
                })}
              </div>
            )}
          </div>
          <DialogFooter>
            {ctPickerFor?.class_teacher_id && (
              <Button variant="outline" className="text-destructive" onClick={() => assignCT(ctPickerFor.id, null)} data-testid="cs-ct-clear">
                Clear class teacher
              </Button>
            )}
            <Button variant="outline" onClick={() => setCtPickerFor(null)}>Cancel</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </PanelShell>
  );
}

function PanelShell({ title, count, action, children }) {
  return (
    <div className="border border-border rounded-md bg-card">
      <div className="px-5 py-4 border-b border-border flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-2">
          <div className="font-display font-bold">{title}</div>
          <Badge variant="outline" className="text-[10px]">{count}</Badge>
        </div>
        {action}
      </div>
      {children}
    </div>
  );
}
function Empty({ msg }) {
  return <div className="p-10 text-center text-sm text-muted-foreground">{msg}</div>;
}
function Loading() {
  return <div className="p-8 text-center text-muted-foreground"><Loader2 className="h-4 w-4 animate-spin inline" /></div>;
}
function Field({ label, children }) {
  return (
    <div className="space-y-1.5">
      <Label className="text-xs font-semibold uppercase tracking-wider">{label}</Label>
      {children}
    </div>
  );
}
